import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { CarteAction } from "./CarteAction";

const Bloc = ({ titre, testId, children }) => (
  <section className="at-card p-5 space-y-3 min-w-0" data-testid={testId}>
    <h2 className="text-base md:text-lg font-bold">{titre}</h2>
    {children}
  </section>
);

export const Autopsie = ({ peutLancer }) => {
  const [a, setA] = useState(undefined);
  useEffect(() => { api.get("/autopsie").then((r) => setA(r.data.autopsie)).catch(() => setA(null)); }, []);
  const lancer = async () => { try { const { data } = await api.post("/autopsie"); setA(data.autopsie); toast.success("Autopsie mise à jour."); } catch (e) { toast.error(messageErreur(e)); } };
  if (a === undefined) return null;
  return (
    <>
      <Bloc titre="Recommandation de la semaine" testId="assistant-strategy">
        {!a ? <p className="text-sm at-muted">Préparée chaque lundi matin avec l'autopsie.</p> : a.recommandations.map((r, i) => (
          <div key={i} className="at-soft p-3 text-sm" data-testid={`strategy-${i}`}><p className="font-bold">{r.titre}</p><p className="break-words">{r.texte}</p></div>
        ))}
      </Bloc>
      <Bloc titre="Autopsie des dossiers perdus" testId="assistant-autopsy">
        {a && <p className="text-xs at-muted">Semaine {a.semaine.split("-S")[1]} · {formatDateHeure(a.cree_le)} · simulation</p>}
        {a && a.dossiers.length === 0 && <p className="text-sm at-muted" data-testid="autopsy-empty">Aucun dossier perdu ou annulé cette semaine.</p>}
        {a?.dossiers.map((d) => (
          <Link key={d.fiche_id} to={`/fiches/${d.fiche_id}`} className="at-soft p-3 block text-sm" data-testid={`autopsy-file-${d.fiche_id}`}>
            <p className="font-bold">{d.nom} · cassé chez : {d.categorie_label}</p>
            <p className="text-xs at-muted">Étape : {d.etape_rupture} · {d.operation}</p>
            <p className="break-words">{d.pourquoi}</p>
          </Link>
        ))}
        {a?.corrections.map((c) => (
          <div key={c.cause} className="space-y-2" data-testid={`autopsy-fix-${c.cause}`}>
            <p className="text-sm font-bold">Correction · {c.label} ({c.nb} dossier{c.nb > 1 ? "s" : ""}) : {c.correction}</p>
            {c.proposition && <CarteAction action={c.proposition} />}
          </div>
        ))}
        {peutLancer && <button type="button" onClick={lancer} className="at-btn at-btn-ghost w-full" data-testid="autopsy-run"><RefreshCw size={15} />Relancer l'autopsie maintenant</button>}
      </Bloc>
    </>
  );
};

const Appel = ({ a }) => (
  <details className="at-soft p-3 text-sm" data-testid={`replay-call-${a.id}`}>
    <summary className="cursor-pointer min-h-[44px] flex items-center justify-between gap-2">
      <span className="min-w-0"><b>{a.rang === "meilleur" ? "Le meilleur" : "À retravailler"}</b> · {a.agent} → {a.contact}</span>
      <span className="font-bold shrink-0" style={{ color: a.note >= 8 ? "var(--success)" : "var(--danger)" }}>{a.note}/10</span>
    </summary>
    <p className="mt-1 break-words">{a.resume}</p>
    <ul className="list-disc pl-5 text-xs at-muted my-1">{a.points.map((p) => <li key={p}>{p}</li>)}</ul>
    <p className="text-xs whitespace-pre-line break-words">{a.transcription}</p>
  </details>
);

export const AppelsAReecouter = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/appels-a-reecouter").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <Bloc titre="Appels à réécouter" testId="assistant-replay">
      <p className="text-xs at-muted">{d.periode === "aujourd'hui" ? "Appels du jour" : "Pas encore assez d'appels aujourd'hui : derniers appels"} · transcription à lire, {d.ecoute ? "écoute au lot 7" : ""}</p>
      {!d.meilleur && <p className="text-sm at-muted">Aucun appel analysé pour l'instant.</p>}
      {d.meilleur && <Appel a={d.meilleur} />}
      {d.moins_bons.map((a) => <Appel key={a.id} a={a} />)}
    </Bloc>
  );
};
